import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Edit, Loader, History } from 'lucide-react';
import { useRoadmapData } from '../hooks/useRoadmapData';
import { Category, TimePeriod } from '../types/roadmap';
import * as Icons from 'lucide-react';
import { getVisibleTimePeriods, getTimePeriodConfig } from '../utils/timePeriods';
import { getSettings } from '../utils/settings';

const PIAlignmentReadOnly: React.FC = () => {
  const { categories, tasks, loading, error } = useRoadmapData();
  const settings = getSettings();
  const currentProject = useRoadmapData().currentProject;
  const [view, setView] = useState<'categories' | 'full'>('categories');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showHistorical, setShowHistorical] = useState(false);
  const [showFuture, setShowFuture] = useState(false);

  const visiblePeriods = getVisibleTimePeriods(tasks, showHistorical, showFuture);

  const getIconComponent = (iconName: string) => {
    return (Icons as any)[iconName] || Icons.Box;
  };

  const getTasksForCategory = (categoryId: string) => {
    const categoryTasks = tasks.filter(t => t.category_id === categoryId);
    const result: Record<string, any[]> = {};

    visiblePeriods.forEach(period => {
      result[period.id] = categoryTasks
        .filter(t => t.time_period === period.id)
        .sort((a, b) => a.display_order - b.display_order);
    });

    return result;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
        <div className="text-center text-white">
          <Loader className="animate-spin mx-auto mb-4" size={48} />
          <p>Loading roadmap data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
        <div className="bg-red-600 text-white p-6 rounded-lg max-w-lg">
          <h2 className="text-xl font-bold mb-2">Error Loading Roadmap</h2>
          <p className="mb-4">{error}</p>
          <p className="text-sm opacity-90">
            Make sure the backend API server is running:
            <code className="block mt-2 bg-red-700 p-2 rounded">
              cd backend && ./start_server.sh
            </code>
          </p>
        </div>
      </div>
    );
  }

  const CategoryCard: React.FC<{ category: Category; index: number }> = ({ category, index }) => {
    const Icon = getIconComponent(category.icon_name);
    const isExpanded = expandedCategory === category.id;
    const categoryTasks = getTasksForCategory(category.id);

    return (
      <div
        className={`transform transition-all duration-500 ${
          view === 'categories' ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        style={{ transitionDelay: `${index * 100}ms` }}
      >
        <div
          className={`bg-gradient-to-r ${category.color} rounded-lg p-3 cursor-pointer hover:shadow-xl transition-all duration-300`}
          onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
        >
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-3">
              <div className="bg-white bg-opacity-25 rounded-full p-2">
                <Icon size={20} />
              </div>
              <div>
                <h3 className="text-base font-bold">{category.title}</h3>
                <p className="text-xs opacity-90">{category.description}</p>
              </div>
            </div>
            <ChevronRight
              size={20}
              className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
          </div>

          {isExpanded && (
            <div className="mt-4 bg-white bg-opacity-15 rounded p-4 animate-fade-in">
              <div
                className="grid gap-4 text-white"
                style={{ gridTemplateColumns: `repeat(${visiblePeriods.length}, minmax(0, 1fr))` }}
              >
                {visiblePeriods.map(period => (
                  <div key={period.id}>
                    <h4 className="font-semibold mb-2">{period.label}</h4>
                    <ul className="text-sm space-y-1 opacity-90">
                      {(categoryTasks[period.id] || []).map((task: any) => (
                        <li key={task.id}>• {task.title}</li>
                      ))}
                      {(categoryTasks[period.id] || []).length === 0 && (
                        <li className="italic opacity-60">No tasks</li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const FullRoadmapView: React.FC = () => {
    const gridStyle = {
      gridTemplateColumns: `minmax(150px, 1fr) repeat(${visiblePeriods.length}, minmax(0, 1fr))`
    };

    return (
      <div className={`transition-all duration-700 ${view === 'full' ? 'opacity-100' : 'opacity-0 pointer-events-none absolute'}`}>
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="mb-6 bg-gradient-to-r from-gray-800 to-gray-900 text-white p-4 rounded">
            <div className="grid gap-4 text-center" style={gridStyle}>
              <div className="font-bold text-lg">Swimlane</div>
              {visiblePeriods.map(period => (
                <div
                  key={period.id}
                  className={`font-bold text-lg ${period.color.headerBg} rounded p-2`}
                >
                  {period.label}
                </div>
              ))}
            </div>
          </div>

          {categories.map((category, index) => {
            const Icon = getIconComponent(category.icon_name);
            const categoryTasks = getTasksForCategory(category.id);

            return (
              <div
                key={category.id}
                className="mb-4 animate-slide-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="grid gap-4" style={gridStyle}>
                  <div className={`bg-gradient-to-r ${category.color} text-white rounded-lg p-4 flex items-center space-x-3`}>
                    <Icon size={24} />
                    <span className="font-semibold text-sm">{category.title}</span>
                  </div>

                  {visiblePeriods.map(period => {
                    const periodTasks = categoryTasks[period.id] || [];
                    return (
                      <div key={period.id} className="space-y-2">
                        {periodTasks.map((task: any) => (
                          <div
                            key={task.id}
                            className={`${period.color.bg} border-l-4 ${period.color.border} rounded p-2 text-xs`}
                          >
                            {task.title}
                          </div>
                        ))}
                        {periodTasks.length === 0 && (
                          <div className="text-xs text-gray-400 italic p-2">No tasks</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-800 to-gray-900 p-4">
      <div className="w-full">
        <div className="text-center mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className="w-32" /> {/* Spacer */}

            <div className="flex-1 text-center">
              <h1 className="text-3xl font-bold text-white mb-1">
                {currentProject?.name || 'PVQ.ai'} PI Roadmap
              </h1>
              <p className="text-gray-300 mb-3 text-sm">Program Increment Planning</p>
            </div>

            <Link
              to="/roadmap/edit"
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:shadow-lg transition-all text-sm font-semibold w-32"
            >
              <Edit size={18} />
              Edit
            </Link>
          </div>

          <div className="flex justify-center space-x-4 mb-3">
            <button
              onClick={() => {
                setExpandedCategory(null);
                setView('categories');
              }}
              className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                view === 'categories'
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Category Overview
            </button>
            <button
              onClick={() => setView('full')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all text-sm ${
                view === 'full'
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Full Roadmap
            </button>
          </div>

          {/* Show Historical and Future Checkboxes */}
          <div className="flex justify-center gap-4">
            <label className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-10 rounded-lg cursor-pointer hover:bg-opacity-20 transition-all">
              <input
                type="checkbox"
                checked={showHistorical}
                onChange={(e) => setShowHistorical(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <History size={16} className="text-gray-300" />
              <span className="text-sm font-medium text-gray-300">Show Historical Periods (PI5)</span>
            </label>
            <label className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-10 rounded-lg cursor-pointer hover:bg-opacity-20 transition-all">
              <input
                type="checkbox"
                checked={showFuture}
                onChange={(e) => setShowFuture(e.target.checked)}
                className="w-4 h-4 rounded border-gray-300"
              />
              <span className="text-sm font-medium text-gray-300">Show Future PIs (PI7+)</span>
            </label>
          </div>
        </div>

        {view === 'categories' && (
          <div className="space-y-2">
            {categories.map((category, index) => (
              <CategoryCard key={category.id} category={category} index={index} />
            ))}
          </div>
        )}

        <FullRoadmapView />
      </div>
    </div>
  );
};

export default PIAlignmentReadOnly;
